import React, { FC, useCallback, useEffect, useReducer, useRef } from 'react';

import {
  DataProvider,
  MultiBlockPolicyCreateAndDeployRequest,
  PolicyApiCreateAndDeployMultiBlockPolicyRequest,
  PolicyApiPreviewMultiBlockPolicyDdlRequest,
  PolicyApiUpdateAndDeployMultiBlockPolicyRequest,
  SubjectConditionOperatorEnum,
  SubjectSelectionEntityTypeEnum,
  SubjectSelectionSelectionLogicEnum,
} from '@ariksa/abac/api';
import { CloudEnvironmentType } from '@ariksa/compliance-policies/api';
import { CloudProvider, NativeResources } from '@ariksa/inventory-core';
import { InsightV2ApiInsightV2AllRequest } from '@ariksa/inventory-core/api';
import { Box, HStack, useDisclosure } from '@chakra-ui/react';
import {
  each,
  filter,
  find,
  includes,
  isEmpty,
  replace,
  some,
  toArray,
} from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { QueryStatus } from 'services/types';
import { customTheme } from 'theme';
import { v4 as uuidv4 } from 'uuid';

import { InventoryService } from 'api/services';
import { Card, FormActionErrorMessage, getIcon } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { Form, FormAction } from 'components/DataEntry';
import { IconTypes } from 'components/Icons';
import { DataPolicyResourceConditions } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Criteria/Conditions/DataPolicyResourceConditions';
import { OktaIdentitiesUsingRoleModal } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Criteria/Conditions/OktaIdentitiesUsingRoleModal';
import { DataPolicyAccessRolesCriteria } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Criteria/DataPolicyAccessRolesCriteria';
import { DataPolicyLabelCriteria } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Criteria/DataPolicyLabelCriteria';
import { DataPolicyFormTitle } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/DataPolicyFormTitle';
import { GeneratedPolicy } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Help/GeneratedPolicy';
import { AddBuckets } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Target/AddBuckets';
import { TargetDataSourcesLabel } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Target/TargetDataSourcesLabel';
import {
  DataPolicyMasks,
  getBlockMaskOptions,
  resolveBlockMaskOption,
  resolveDefaultMaskOption,
} from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/maskOptions';
import { dataPolicyFormStyles } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/styles';
import {
  dataPolicyAwsResourceTypeOptions,
  dataPolicyCloudOptions,
  dataPolicyIdentityProviderOptions,
  dataPolicyQuantifierOptions,
  renderDataPolicyConditionLabel,
} from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/utils';
import { seletMaskingPolicies } from 'containers/Jit/MaskingPolicies/selectors';
import { actions } from 'containers/Jit/MaskingPolicies/slice';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

interface Props {
  action?: FormAction;
}

export const CreateDataPolicy: FC<Props> = props => {
  const { action = 'Add' } = props;
  const dispatch = useDispatch();
  const {
    structure,
    cloudAccounts,
    dataGroupRoles,
    generatePolicy,
    policyAction,
    multiBlockPolicy,
  } = useSelector(seletMaskingPolicies);
  const { accountMapping } = useCloudAccountId();
  const navigate = useNavigate();
  const identitiesUsingRoleDisclosure = useDisclosure();
  const { setLearnMore } = useKnowledgeBase();
  const requestedResourceKeys = useRef(new Set<string>());

  const [state, updateState] = useReducer(
    (prev, next) => {
      if (typeof next === 'function') {
        // functional update
        return next(prev);
      }
      // normal object merge
      return { ...prev, ...next };
    },
    {
      name: '',
      description: '',
      policyId: '',
      id: uuidv4(),
      cloud: dataPolicyCloudOptions[0],
      accountOptions: [],
      account: {},
      principalType: {},
      principalTypeOptions: [],
      identityProvider: {},
      identityProviderOptions: [],
      dataGroups: [],
      activeDataGroupIndex: 0,
      dataGroupOptions: [],
      conditions: {},
      operators: [],
      policyTypeOptions: [],
      policyType: {},
      resourceOptions: {},
      tagOptions: [],
      regionOptions: [],
      conditionQuantifier: dataPolicyQuantifierOptions[0],
      tag: {},
      defaultMask: {
        label: 'Full Mask',
        value: DataPolicyMasks.FullMask,
      },
      buckets: {},
      isInvalid: false,
      isEdit: false,
      generatePolicy: [],
      resourceType: dataPolicyAwsResourceTypeOptions[0],
      mask: {},
      maskingOptions: [],
      identitiesUsingRoles: {},
      currentRole: {},
    },
  );

  useEffect(() => {
    if (action === 'Update') updateState({ isEdit: true });
  }, [action]);

  //get accounts
  useEffect(() => {
    dispatch(
      actions.getCloudAccounts({
        q: {},
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!state?.account?.value) return;
    dispatch(
      actions.getDataGroups({
        q: { accountId: state?.account?.value, page: 1, size: 100 },
        onSuccess: res => {
          const options = map(res?.items, o => ({
            label: o.name,
            value: o.uuid,
            data: o,
          }));
          updateState({
            dataGroupOptions: options,
            dataGroups: isEmpty(options) ? [] : [options[0]],
          });
        },
      }),
    );
  }, [dispatch, state?.account?.value]);

  // Load only the roles assigned to each selected data group.
  useEffect(() => {
    each(state?.dataGroups, dataGroup => {
      const dataGroupId = dataGroup?.value;
      if (!dataGroupId || dataGroupRoles?.[dataGroupId]) return;

      dispatch(actions.getDataGroupRoles({ q: { dataGroupId } }));
    });
  }, [dispatch, state?.dataGroups, dataGroupRoles]);

  // Conditions can be created before data groups finish loading. Fill the
  // default group once it becomes available so validation matches the UI.
  useEffect(() => {
    if (isEmpty(state?.dataGroupOptions) || isEmpty(state?.conditions)) return;

    updateState(prev => {
      let hasUpdatedConditions = false;
      const conditions = { ...prev.conditions };

      Object.keys(conditions).forEach(conditionId => {
        const condition = conditions[conditionId];
        if (
          !isEmpty(condition?.dataGroups) &&
          !some(condition.dataGroups, dataGroup => isEmpty(dataGroup))
        ) {
          return;
        }

        conditions[conditionId] = {
          ...condition,
          dataGroups: {
            [uuidv4()]: prev.dataGroupOptions[0],
          },
        };
        hasUpdatedConditions = true;
      });

      return hasUpdatedConditions ? { ...prev, conditions } : prev;
    });
  }, [state?.dataGroupOptions, state?.conditions]);

  //set data groups in edit mode
  useEffect(() => {
    if (
      !!state?.account &&
      !!state?.isEdit &&
      !isEmpty(state?.dataGroupOptions)
    ) {
      updateState({
        dataGroups: filter(state?.dataGroupOptions, o =>
          includes(
            multiBlockPolicy.data?.policy_blocks?.flatMap(
              block => block?.data_group_ids ?? [],
            ),
            o.value,
          ),
        ),
      });
    }
  }, [
    state?.dataGroupOptions,
    state?.account,
    state?.isEdit,
    multiBlockPolicy.data,
  ]);

  //set cloud account options
  useEffect(() => {
    const accounts = map(cloudAccounts.data, o => ({
      label:
        accountMapping?.[o?.account_id]?.name +
        ' (' +
        accountMapping?.[o?.account_id]?.cloud_account_id +
        ')',
      value: o?.account_id,
      data: o,
      icon: getIcon(o?.provider!),
    }));
    updateState({ accountOptions: accounts });
  }, [cloudAccounts.data, accountMapping]);

  //set an account
  useEffect(() => {
    if (state?.isEdit)
      updateState({
        account: find(
          state?.accountOptions,
          o =>
            (multiBlockPolicy.data?.deployed_accounts?.[0] ??
              multiBlockPolicy.data?.account_id) === o.value,
        ),
      });
    else updateState({ account: state?.accountOptions?.[0] });
  }, [state?.accountOptions, state?.isEdit, multiBlockPolicy.data]);

  //set name, description, policy id in edit mode
  useEffect(() => {
    if (state?.isEdit && !isEmpty(multiBlockPolicy.data))
      updateState({
        name: multiBlockPolicy.data.policy_name,
        description: multiBlockPolicy.data.description,
        policyId: multiBlockPolicy.data.policy_id,
      });
  }, [state?.isEdit, multiBlockPolicy.data]);

  const onClickAddCondition = useCallback(
    (
      principalType: Record<string, any>,
      maskingOptions?: Record<string, any>,
      policyTypeOptions?: Record<string, any>,
      dataGroup?: Record<string, any>,
      roleOptions?: Record<string, any>[],
      selectDefaultRoleInEdit = false,
    ) => {
      const id = uuidv4();
      const dataGroupId = uuidv4();
      const defaultRole =
        !state?.isEdit || selectDefaultRoleInEdit
          ? roleOptions?.[0] ??
            (state?.account?.data?.provider === CloudProvider.Snowflake
              ? {}
              : state?.resourceOptions?.SnowflakeRoles?.[0] ?? {})
          : {};
      const attributes = map(principalType?.data?.attributes, (value, key) => ({
        ...value,
        key,
      }));

      updateState(prev => {
        return {
          ...prev,
          conditions: {
            ...prev.conditions,
            [id]: {
              key: attributes[0]?.key,
              id,
              ...attributes[0],
              operator: { label: 'is', value: 'is' },
              role: defaultRole,
              selectDefaultRoleInEdit,
              dataGroups: {
                [dataGroupId]:
                  dataGroup ??
                  state?.dataGroups?.[0] ??
                  state?.dataGroupOptions?.[0] ??
                  null,
              },
              policyType:
                policyTypeOptions?.[0] ?? state?.policyTypeOptions?.[0],
              mask:
                Object.values(prev.conditions ?? {}).find(
                  existingCondition =>
                    existingCondition?.role?.value === defaultRole?.value &&
                    existingCondition?.mask,
                )?.mask ??
                maskingOptions?.[0] ??
                state?.maskingOptions[0],
            },
          },
        };
      });
    },
    [
      state?.policyTypeOptions,
      state?.maskingOptions,
      state?.dataGroupOptions,
      state?.dataGroups,
      state?.account?.data?.provider,
      state?.resourceOptions,
      state?.isEdit,
    ],
  );

  const onClickAddBucket = () => {
    const id = uuidv4();

    updateState(prev => {
      return {
        ...prev,
        buckets: {
          ...prev.buckets,
          [id]: {
            id,
            value: { label: '*', value: 'all' },
            region: state?.regionOptions?.[0],
            path: '/*',
          },
        },
      };
    });
  };

  //get structure
  useEffect(() => {
    if (!state?.account?.data?.provider) return;
    dispatch(
      actions.getStructure({
        q: {
          provider:
            state?.account?.data?.provider === CloudProvider.Aws
              ? DataProvider.AwsS3
              : DataProvider.Snowflake,
        },
        onSuccess: res => {
          //set principal type options
          const principalTypeOptions = map(res?.subjects, o => ({
            label: o.display_name,
            value: o?.type ?? o?.resource_type,
            icon: getIcon(IconTypes.Role),
            data: o,
          }));

          //set policy type options
          let policyTypeOptions: Record<string, any>[] = map(
            res?.policy_types,
            (o: Record<string, any>) => ({
              label: o?.display_name,
              value: o?.type,
              data: o,
            }),
          );
          if (res?.provider !== CloudProvider.Snowflake) {
            policyTypeOptions = map(
              res?.policy_types?.[0]?.actions?.permissions,
              (o: Record<string, any>) => ({
                label: o?.display_name,
                value: o?.permission,
                data: o,
              }),
            );
          }

          //set identity provider options
          let identityProviderOptions: Record<string, any> = map(
            res?.identity_providers,
            (o: Record<string, any>) => ({
              label: o.display_name,
              value: o.type,
              icon: getIcon(
                o.type,
                undefined,
                getIcon(CloudEnvironmentType.IdentityProvider),
              ),
              data: o,
            }),
          );
          if (isEmpty(identityProviderOptions))
            identityProviderOptions = dataPolicyIdentityProviderOptions;

          //set masking options
          // Find the policy type that has masking methods. Its position and
          // its name are not the same for each provider: Snowflake calls it
          // `column_masking`, and AWS S3 calls it `data_masking`.
          const maskingPolicyType = find(
            res?.policy_types,
            (o: Record<string, any>) => !isEmpty(o?.actions?.methods),
          );
          let maskingOptions = map(maskingPolicyType?.actions?.methods, o => ({
            label: o?.display_name,
            value: o?.method,
          }));
          // Snowflake role blocks may override up to FULL_MASK (mask a role more
          // strictly than a weaker default). Provider metadata advertises only
          // NO_MASK/ANONYMIZATION/PARTIAL, so add FULL_MASK here; other providers
          // keep exactly what they advertise.
          if (res?.provider === CloudProvider.Snowflake) {
            maskingOptions = getBlockMaskOptions(maskingOptions);
          }

          updateState({
            principalTypeOptions,
            principalType: principalTypeOptions?.[0],
            operators: map(res?.operators, o => ({
              label: o?.display_name,
              value: o?.operator,
            })),
            policyTypeOptions,
            policyType: policyTypeOptions?.[0],
            conditions: {},
            identityProviderOptions,
            identityProvider: identityProviderOptions?.[0],
            maskingOptions,
            mask: maskingOptions?.[0],
          });
          onClickAddCondition(
            principalTypeOptions?.[0],
            maskingOptions,
            policyTypeOptions,
          );
        },
      }),
    );
  }, [dispatch, state?.account?.value, state?.account?.data?.provider]); //do not add onClickAddCondition dependency, it is triggering multiple api call

  //set principal type in edit mode
  useEffect(() => {
    if (!!state?.isEdit && !!state?.account) {
      updateState({
        principalType:
          find(
            state?.principalTypeOptions,
            o =>
              o.value ===
              multiBlockPolicy.data?.policy_blocks?.[0]?.subject_selection
                ?.entity_type,
          ) ?? state?.principalTypeOptions[0], //TODO: Fix value selection
      });
    }
  }, [
    state?.principalTypeOptions,
    state?.isEdit,
    state?.account,
    multiBlockPolicy.data,
  ]);

  //set conditions
  useEffect(() => {
    if (
      !!state?.account &&
      !!state?.isEdit &&
      !!state?.principalType &&
      !isEmpty(state?.resourceOptions)
    ) {
      let conditions: Record<string, any> = {};
      const attributes = map(
        state?.principalType?.data?.attributes,
        (value, key) => ({
          ...value,
          key,
        }),
      );
      each(multiBlockPolicy.data?.policy_blocks, block => {
        each(block?.data_group_ids, dataGroupId => {
          const dataGroup = find(
            state?.dataGroupOptions,
            option => option?.value === dataGroupId,
          );
          if (!dataGroup) return;

          const roleValues =
            block?.subject_selection?.conditions?.[0]?.value ?? [];
          each(roleValues, roleValue => {
            const id = uuidv4();
            conditions[id] = {
              id,
              key: attributes[0]?.key,
              ...attributes[0],
              operator: { label: 'is', value: 'is' },
              role: find(
                state?.resourceOptions?.SnowflakeRoles,
                r => r?.value === roleValue,
              ),
              dataGroups: { [uuidv4()]: dataGroup },
              policyType:
                find(
                  state?.policyTypeOptions,
                  option => option?.value === block?.policy_config?.type,
                ) ?? state?.policyTypeOptions?.[0],
              mask:
                resolveBlockMaskOption(
                  block?.policy_config?.method,
                  state?.maskingOptions,
                ) ?? state?.maskingOptions[0],
            };
          });
        });
      });
      updateState({ conditions });
    }
  }, [
    state?.account,
    state?.isEdit,
    state?.principalType,
    multiBlockPolicy.data,
    state?.resourceOptions,
    state?.dataGroupOptions,
    state?.policyTypeOptions,
    state?.maskingOptions,
  ]);

  //get tag key values for snowflake and rds and set options
  useEffect(() => {
    if (
      !!state?.account?.value &&
      (state?.account?.data?.provider === CloudProvider.Snowflake ||
        (state?.account?.data?.provider === CloudProvider.Aws &&
          state?.resourceType?.value ===
            NativeResources.RelationalDatabaseService))
    ) {
      const tagName = multiBlockPolicy.data?.tag_name;

      // An existing policy already contains the complete tag name. Build the
      // selected option locally instead of fetching all tags just to find it.
      if (state?.isEdit && tagName) {
        const separatorIndex = tagName.lastIndexOf('.');
        const keyDisplayName =
          separatorIndex === -1 ? tagName : tagName.slice(separatorIndex + 1);
        const tagKeyPrefix =
          separatorIndex === -1 ? undefined : tagName.slice(0, separatorIndex);
        const option = {
          label: (
            <HStack>
              <Box>{keyDisplayName}</Box>
              {tagKeyPrefix && (
                <Box color={customTheme.colors.gray['200']}>
                  {replace(tagKeyPrefix, '.', '/')}
                </Box>
              )}
            </HStack>
          ),
          value: {
            key: tagName,
            key_display_name: keyDisplayName,
            tag_key_prefix: tagKeyPrefix,
          },
        };

        updateState({ tagOptions: [option], tag: option });
        return;
      }

      dispatch(
        actions.getTagKeyValues({
          q: { accountId: state?.account?.value },
          onSuccess: res => {
            let options: Record<string, any>[] = [];
            each(
              filter(res?.tags, o => isEmpty(o?.attached_policies)),
              o => {
                options.push({
                  label: (
                    <HStack>
                      <Box>{o.key_display_name}</Box>
                      <Box color={customTheme.colors.gray['200']}>
                        {replace(o.tag_key_prefix!, '.', '/')}
                      </Box>
                    </HStack>
                  ),
                  value: o,
                });
              },
            );
            updateState({
              tagOptions: options,
              tag: options?.[0],
            });
          },
        }),
      );
    }
  }, [
    state?.account?.value,
    state?.account?.data?.provider,
    dispatch,
    state?.resourceType?.value,
    state?.isEdit,
    multiBlockPolicy.data?.tag_name,
  ]);

  //set label(tag) in edit mode
  useEffect(() => {
    if (!!state?.account && !isEmpty(state?.tagOptions)) {
      updateState({
        tag:
          find(
            state?.tagOptions,
            o => o?.value?.key === multiBlockPolicy.data?.tag_name,
          ) || state?.tagOptions[0],
        defaultMask: resolveDefaultMaskOption(
          multiBlockPolicy.data?.default_mask_config?.method,
          state?.maskingOptions,
        ),
      });
    }
  }, [
    state?.account,
    state?.isEdit,
    state?.tagOptions,
    state?.maskingOptions,
    multiBlockPolicy.data,
  ]);

  //get regions for s3
  useEffect(() => {
    if (
      state?.account?.data?.provider === CloudProvider.Aws &&
      state?.resourceType?.value === NativeResources.SimpleStorageService
    ) {
      dispatch(
        sharedStateActions.getRegions({
          q: {
            nativeResource: NativeResources.SimpleStorageService,
            accountId: state?.account?.value,
          },
          onSuccess: res => {
            updateState({
              regionOptions: map(res, o => ({ label: o, value: o })),
            });
          },
        }),
      );
    }
  }, [dispatch, state?.account, state?.resourceType]);

  //get resource options
  const setResourceOptions = (
    res: Record<string, any>,
    resourceType: string,
    isScope: boolean,
  ) => {
    const options = map(res?.data?.items, r => ({
      label: r?.name ?? r?.resource_id,
      value: r?.resource_id,
      icon: getIcon(
        resourceType === NativeResources.SnowflakeRoles
          ? NativeResources.SnowflakeRoles
          : r?.native_name,
      ),
      data: r,
    }));
    updateState(prev => ({
      ...prev,
      resourceOptions: {
        ...prev.resourceOptions,
        [resourceType]: isScope
          ? [{ label: 'All', value: 'all' }, ...options]
          : resourceType === NativeResources.SimpleStorageService
          ? [{ label: '*', value: 'all' }, ...options]
          : options,
      },
    }));
  };

  //get resources like databases, tables, user or role names
  const getResources = useCallback(
    (
      resourceType,
      isScope = false,
      parentResourceType = undefined,
      parentUUID = undefined,
    ) => {
      const insightPayload: InsightV2ApiInsightV2AllRequest = {
        insightV2RequestBase: {
          resource_type: resourceType as NativeResources,
          account_id: [state?.account?.value],
          parent_uuid: parentUUID,
          parent_resource_type: parentResourceType! as NativeResources,
        },
      };
      const accountId = state?.account?.value;
      const requestKey = JSON.stringify([
        accountId,
        resourceType,
        parentResourceType,
        parentUUID,
      ]);
      if (!accountId || requestedResourceKeys.current.has(requestKey)) return;
      requestedResourceKeys.current.add(requestKey);

      dispatch(
        actions.getResources({
          q: insightPayload,
          status: QueryStatus.pending,
        }),
      );
      InventoryService.InsightV2.insightV2All(insightPayload)
        .then(res => {
          //set resource options
          setResourceOptions(res, resourceType, isScope);
          dispatch(
            actions.getResources({
              q: insightPayload,
              status: QueryStatus.fulfilled,
              data: res.data?.items,
            }),
          );
        })
        .catch(() => requestedResourceKeys.current.delete(requestKey));
    },
    [dispatch, state?.account?.value],
  );

  //get resources for conditioned resource types for Snowflake
  useEffect(() => {
    if (!!state?.account?.value && !!state?.principalType) {
      each(state?.principalType?.data?.attributes, (o: Record<string, any>) => {
        !!o?.resource_type && getResources(o?.resource_type);
      });
    }
  }, [state?.principalType, state?.account?.value, getResources]);

  //get resources for AWS
  useEffect(() => {
    if (state?.account?.data?.provider === CloudProvider.Aws) {
      getResources(state?.resourceType?.value);
    }
  }, [
    state?.resourceType?.value,
    state?.account?.value,
    state?.account?.data?.provider,
    getResources,
  ]);

  const onDeleteAttribute = (item, field) =>
    updateState(prev => {
      const { [item.id]: _, ...rest } = prev?.[field];
      return {
        ...prev,
        [field]: rest,
        isInvalid: false,
      };
    });

  const updateConditions = (item, key, value) =>
    updateState(prev => {
      const roleValue = key === 'role' ? value?.value : item?.role?.value;
      const sharedMask =
        key === 'role'
          ? Object.values(prev.conditions ?? {}).find(
              condition =>
                condition?.id !== item?.id &&
                condition?.role?.value === roleValue &&
                condition?.mask,
            )?.mask
          : undefined;

      const conditions = Object.entries(prev.conditions ?? {}).reduce(
        (nextConditions, [conditionId, condition]) => {
          const isUpdatedCondition = conditionId === item.id;
          const hasSameRole =
            key === 'mask' &&
            !!roleValue &&
            condition?.role?.value === roleValue;

          nextConditions[conditionId] = {
            ...condition,
            ...(isUpdatedCondition ? { [key]: value } : {}),
            ...(isUpdatedCondition && sharedMask ? { mask: sharedMask } : {}),
            ...(hasSameRole ? { mask: value } : {}),
          };
          return nextConditions;
        },
        {},
      );

      return {
        ...prev,
        conditions,
        isInvalid: false,
      };
    });

  const onSubmit = type => {
    const blocksByKey = Object.values(state.conditions ?? {}).reduce(
      (blocks, condition) => {
        const roleValue = condition?.role?.value;
        const dataGroupIds = Array.from(
          new Set(
            toArray(condition?.dataGroups)
              .map(dataGroup => dataGroup?.value)
              .filter(Boolean),
          ),
        );

        if (!roleValue || !dataGroupIds.length) return blocks;

        dataGroupIds.forEach(dataGroupId => {
          const blockKey = [
            dataGroupId,
            condition?.key,
            condition?.policyType?.value,
            condition?.mask?.value,
          ].join(':');
          const existingBlock = blocks[blockKey];

          if (existingBlock) {
            existingBlock.subject_selection.conditions[0].value = [
              ...new Set([
                ...existingBlock.subject_selection.conditions[0].value,
                roleValue,
              ]),
            ];
            return;
          }

          blocks[blockKey] = {
            subject_selection: {
              entity_type: SubjectSelectionEntityTypeEnum.Role,
              selection_logic: SubjectSelectionSelectionLogicEnum.Any,
              conditions: [
                {
                  attribute: condition.key,
                  operator: SubjectConditionOperatorEnum.In,
                  value: [roleValue],
                },
              ],
            },
            data_group_ids: [dataGroupId],
            policy_config: {
              type: condition?.policyType?.value,
              method: condition?.mask?.value,
            },
          };
        });

        return blocks;
      },
      {},
    );
    const commonPayload: MultiBlockPolicyCreateAndDeployRequest = {
      account_id: state?.account?.value,
      policy_id: state?.policyId,
      policy_name: state?.name,
      provider: state?.account?.data?.provider,
      target_cloud_account: state?.account?.data?.cloud_account_id,
      tag_name: state?.tag?.value?.key,
      default_mask_config: {
        method: state?.defaultMask?.value ?? DataPolicyMasks.FullMask,
      },
      description: state?.description,
      policy_blocks: Object.values(blocksByKey),
    };

    const payload: PolicyApiCreateAndDeployMultiBlockPolicyRequest = {
      multiBlockPolicyCreateAndDeployRequest: commonPayload,
    };

    const updatePayload: PolicyApiUpdateAndDeployMultiBlockPolicyRequest = {
      policyId: state?.policyId,
      multiBlockPolicyCreateAndDeployRequest: commonPayload,
    };

    const policyPreview: PolicyApiPreviewMultiBlockPolicyDdlRequest = {
      multiBlockPolicyPreviewRequest: commonPayload,
    };

    const onSuccess = () => {
      updateState({ isInvalid: false });
      navigate('/jit/data/policy');
    };
    const onError = () => updateState({ isInvalid: true });

    if (type === 'submit') {
      if (state?.isEdit)
        dispatch(
          actions.updateAndDeployMultiBlockPolicy({
            q: updatePayload,
            onSuccess,
            onError,
          }),
        );
      else dispatch(actions.createPolicy({ q: payload, onSuccess, onError }));
    } else
      dispatch(
        actions.generatePolicy({
          q: policyPreview,
          onSuccess: res => {
            updateState({
              isInvalid: false,
              generatePolicy: res?.ddl_statements,
            });
          },
          onError,
        }),
      );
  };

  const onChangeAccount = option => {
    const id = uuidv4();
    let buckets: Record<string, any> = {};
    if (
      option?.data?.provider === CloudProvider.Aws &&
      state?.resourceType?.value === NativeResources.SimpleStorageService
    )
      buckets = {
        [id]: {
          path: '/*',
          region: state?.regionOptions?.[0],
          id,
          value: { label: '*', value: 'all' },
        },
      };
    updateState({
      account: option,
      tag: {},
      principalType: {},
      policyType: {},
      buckets,
      policyId:
        option?.data?.provider?.toLowerCase() +
        '-policy-' +
        state?.name +
        '-' +
        state?.id,
      isInvalid: false,
    });
  };

  return (
    <Card styles={{ cardContent: { pl: 32, ml: 2 } }}>
      <Form
        title={<DataPolicyFormTitle action={action} />}
        schema={{
          policyNameAndId: {
            type: 'object',
            properties: {
              policy_name: {
                type: 'text',
                label: 'Policy Name',
                value: state?.name,
                onChange: val => {
                  updateState({
                    name: val,
                    isInvalid: false,
                  });
                  if (!state?.isEdit)
                    updateState({
                      policyId:
                        state?.account?.data?.provider?.toLowerCase() +
                        '-policy-' +
                        val +
                        '-' +
                        state?.id,
                    });
                },
                autoFocus: true,
                isDisabled: !state?.account?.value,
                placeholder: 'Enter policy name',
                isRequired: true,
                isHelpOpen: true,
                helpComponent: (
                  <GeneratedPolicy
                    generatedPolicy={state?.generatePolicy}
                    isInvalid={state?.isInvalid}
                  />
                ),
              },
              policy_id: {
                type: 'text',
                label: 'Policy ID',
                isDisabled: true,
                value: state?.policyId,
                placeholder: 'This is auto-generated',
              },
            },
            styles: {
              propertyContainer: [{ w: '50%' }, { w: '50%' }],
            },
          },
          description: {
            type: 'textArea',
            label: 'Description',
            placeholder:
              'This description is to codify what actions or policies need to be ' +
              'enforced when a certain condition is detected or ' +
              'found in the cloud environment',
            isRequired: true,
            onChange: val =>
              updateState({ description: val, isInvalid: false }),
            value: state?.description,
          },
          accountAndResourceType: {
            type: 'object',
            properties: {
              account: {
                type: 'react-select',
                label: 'Account',
                options: state?.accountOptions,
                value: state?.account,
                isLoading: cloudAccounts.isLoading,
                isDisabled: cloudAccounts.isLoading || state?.isEdit,
                onChange: onChangeAccount,
                isRequired: true,
                showIconInValueContainer: true,
                valueContainerIcon: state?.account?.icon,
              },
              ...(state?.account?.data?.provider?.toLowerCase() === 'aws'
                ? {
                    resourceType: {
                      type: 'react-select',
                      label: 'Resource Type',
                      options: dataPolicyAwsResourceTypeOptions,
                      value: state?.resourceType,
                      onChange: val => updateState({ resourceType: val }),
                      showIconInValueContainer: true,
                    },
                  }
                : {}),
            },
            styles: {
              propertyContainer: [{ w: '50%' }, { w: '50%' }],
            },
          },
          entity: {
            type: 'object',
            properties: {
              principalType: {
                type: 'react-select',
                label: 'Principal Type',
                options: state?.principalTypeOptions,
                value: state?.principalType,
                isDisabled: cloudAccounts.isLoading || state?.isEdit,
                isRequired: true,
                onChange: val => {
                  updateState({
                    principalType: val,
                    isInvalid: false,
                    conditions: {},
                  });
                  onClickAddCondition(val);
                },
                isLoading: structure.isLoading,
                showIconInValueContainer: true,
                valueContainerIcon: state?.principalType?.icon,
              },
              identityProvider: {
                type: 'react-select',
                label: 'Identity Provider',
                options: state?.identityProviderOptions,
                value: state?.identityProvider,
                isDisabled: true,
                isLoading: cloudAccounts.isLoading,
                showIconInValueContainer: true,
                valueContainerIcon: state?.identityProvider?.icon,
              },
            },
            styles: {
              propertyContainer: [{ w: '50%' }, { w: '50%' }],
            },
          },
          ...(state?.account?.data?.provider === CloudProvider.Snowflake ||
          (state?.account?.data?.provider === CloudProvider.Aws &&
            state?.resourceType?.value ===
              NativeResources.RelationalDatabaseService)
            ? {
                tags: {
                  type: 'custom',
                  component: () => (
                    <DataPolicyLabelCriteria
                      state={state}
                      updateState={updateState}
                      setLearnMore={setLearnMore}
                    />
                  ),
                },
              }
            : {}),
          ...(state?.account?.data?.provider === CloudProvider.Snowflake
            ? {
                accessRolesAndMasking: {
                  type: 'custom',
                  component: () => (
                    <DataPolicyAccessRolesCriteria
                      state={state}
                      updateConditions={updateConditions}
                      onClickAddCondition={onClickAddCondition}
                      onDeleteCondition={onDeleteAttribute}
                      resourceOptions={state?.resourceOptions}
                      dataGroupRoles={dataGroupRoles}
                      identitiesUsingRoleDisclosure={
                        identitiesUsingRoleDisclosure
                      }
                      updateState={updateState}
                    />
                  ),
                },
              }
            : {
                conditions: {
                  type: 'custom',
                  component: () => (
                    <DataPolicyResourceConditions
                      state={state}
                      onClickAddCondition={onClickAddCondition}
                      updateState={updateState}
                      identitiesUsingRoleDisclosure={
                        identitiesUsingRoleDisclosure
                      }
                    />
                  ),
                },
              }),
          ...(state?.resourceType?.value ===
            NativeResources.SimpleStorageService &&
          state?.account?.data?.provider === CloudProvider.Aws
            ? {
                performActionMessage: {
                  type: 'custom',
                  component: () =>
                    renderDataPolicyConditionLabel(
                      'THEN',
                      'perform the following actions',
                    ),
                },
                policyType: {
                  type: 'react-select',
                  label:
                    'Enforce' +
                    (state?.resourceType?.value ===
                      NativeResources.SimpleStorageService &&
                    state?.account?.data?.provider === CloudProvider.Aws
                      ? ' Permission'
                      : ''),
                  options: state?.policyTypeOptions,
                  value: state?.policyType,
                  onChange: val =>
                    updateState({ policyType: val, isInvalid: false }),
                  isLoading: structure.isLoading,
                  isRequired: true,
                },
                mask: {
                  type: 'react-select',
                  label:
                    state?.resourceType?.value ===
                      NativeResources.RelationalDatabaseService &&
                    state?.account?.data?.provider === CloudProvider.Aws
                      ? 'Apply Mask Policy'
                      : 'Mask Sensitive Data With',
                  options: state?.maskingOptions,
                  value: state?.mask,
                  onChange: val => updateState({ mask: val }),
                  isRequired: true,
                },
              }
            : {}),
          ...(state?.account?.data?.provider === CloudProvider.Snowflake
            ? {}
            : {
                access: {
                  type: 'custom',
                  component: () => (
                    <TargetDataSourcesLabel
                      account={state?.account}
                      resourceType={state?.resourceType}
                      onClickAddBucket={onClickAddBucket}
                    />
                  ),
                },
              }),
          ...(state?.resourceType?.value ===
            NativeResources.SimpleStorageService &&
          state?.account?.data?.provider === CloudProvider.Aws
            ? {
                buckets: {
                  type: 'custom',
                  component: () => (
                    <AddBuckets
                      state={state}
                      updateState={updateState}
                      onDeleteAttribute={onDeleteAttribute}
                    />
                  ),
                },
              }
            : {}),
          ...(state?.isInvalid &&
          (!isEmpty(policyAction.error) || !isEmpty(generatePolicy.error))
            ? {
                validatedMessage: {
                  type: 'custom',
                  component: () => (
                    <FormActionErrorMessage
                      description={policyAction.error?.description}
                    />
                  ),
                },
              }
            : {}),
        }}
        showHelp
        buttonOptions={{
          submit: {
            name: action === 'Add' ? 'CREATE' : 'UPDATE',
            isLoading: policyAction.isLoading,
            isDisabled:
              !state?.name ||
              !state?.description ||
              (state?.account?.data?.provider === CloudProvider.Snowflake &&
                isEmpty(state?.dataGroups)) ||
              some(state?.conditions, o =>
                some(o?.dataGroups, d => isEmpty(d)),
              ) ||
              (state?.account?.data?.provider === CloudProvider.Snowflake &&
                some(state?.conditions, o => isEmpty(o?.role))) ||
              policyAction.isLoading ||
              (state?.account?.data?.provider === CloudProvider.Snowflake &&
                isEmpty(state?.tag)),
            tooltip: !state?.name
              ? 'Please provide name'
              : !state?.description
              ? 'Please provide description'
              : state?.account?.data?.provider === CloudProvider.Snowflake &&
                isEmpty(state?.dataGroups)
              ? 'Please provide one or more data groups'
              : some(state?.conditions, o =>
                  some(o?.dataGroups, d => isEmpty(d)),
                )
              ? 'Please provide one or more data groups'
              : state?.account?.data?.provider === CloudProvider.Snowflake &&
                some(state?.conditions, o => isEmpty(o?.role))
              ? 'Please provide one or more roles'
              : state?.account?.data?.provider === CloudProvider.Snowflake &&
                isEmpty(state?.tag)
              ? 'Please provide one or more criteria'
              : undefined,
          },
          reset: {
            name: 'CANCEL',
            onClick: () => navigate('/jit/data/policy'),
            isVisible: true,
          },
          back: {
            name: 'GENERATE',
            onClick: () => onSubmit('generate'),
            isVisible: false,
            isLoading: generatePolicy.isLoading,
            isDisabled:
              !state?.name ||
              !state?.description ||
              isEmpty(state?.conditions) ||
              some(state?.conditions, o => !o?.value) ||
              policyAction.isLoading,
          },
        }}
        styles={dataPolicyFormStyles}
        handleSubmit={() => onSubmit('submit')}
      />
      {identitiesUsingRoleDisclosure.isOpen && (
        <OktaIdentitiesUsingRoleModal
          isOpen={identitiesUsingRoleDisclosure.isOpen}
          onClose={identitiesUsingRoleDisclosure.onClose}
          role={state?.currentRole}
        />
      )}
      <LearnMore />
    </Card>
  );
};
